import { InvoicePaymentStatusType, InvoiceStatusType } from 'lib/util';
import { Invoice } from 'types';

export type BillingReviewStatus = 'pending' | 'needs_fix' | 'approved' | 'sent' | 'voided';

export type BillingReviewBucket = 'needs_review' | 'needs_fix' | 'approved' | 'sent' | 'all';

const STORAGE_KEY = 'billing.reviewOverrides';

type Overrides = Record<string, BillingReviewStatus>;

const readOverrides = (): Overrides => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return {};
		return JSON.parse(raw) as Overrides;
	} catch {
		return {};
	}
};

const writeOverrides = (overrides: Overrides) => {
	localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
};

export const setBillingReviewOverride = (invoiceId: number, status: BillingReviewStatus) => {
	const overrides = readOverrides();
	overrides[String(invoiceId)] = status;
	writeOverrides(overrides);
};

export const clearBillingReviewOverride = (invoiceId: number) => {
	const overrides = readOverrides();
	delete overrides[String(invoiceId)];
	writeOverrides(overrides);
};

const defaultStatus = (invoice: Invoice): BillingReviewStatus => {
	if (invoice.status === InvoiceStatusType.VOIDED || invoice.reason) {
		return 'voided';
	}
	if (invoice.paymentStatus === InvoicePaymentStatusType.PAID) {
		return 'sent';
	}
	if (invoice.path) {
		return 'approved';
	}
	return 'pending';
};

export const getBillingReviewStatus = (invoice: Invoice): BillingReviewStatus => {
	const override = readOverrides()[String(invoice.id)];
	if (override) return override;
	return defaultStatus(invoice);
};

export const billingReviewStatusLabel = (status: BillingReviewStatus): string => {
	switch (status) {
		case 'pending':
			return 'Needs review';
		case 'needs_fix':
			return 'Needs fix';
		case 'approved':
			return 'Approved';
		case 'sent':
			return 'Sent';
		case 'voided':
			return 'Voided';
		default:
			return status;
	}
};

export const invoiceMatchesBucket = (invoice: Invoice, bucket: BillingReviewBucket): boolean => {
	const status = getBillingReviewStatus(invoice);
	if (bucket === 'all') return true;
	if (bucket === 'needs_review') return status === 'pending';
	if (bucket === 'needs_fix') return status === 'needs_fix';
	if (bucket === 'approved') return status === 'approved';
	if (bucket === 'sent') return status === 'sent';
	return true;
};

export const countByBucket = (invoices: Invoice[]): Record<BillingReviewBucket, number> => {
	return {
		needs_review: invoices.filter((i) => invoiceMatchesBucket(i, 'needs_review')).length,
		needs_fix: invoices.filter((i) => invoiceMatchesBucket(i, 'needs_fix')).length,
		approved: invoices.filter((i) => invoiceMatchesBucket(i, 'approved')).length,
		sent: invoices.filter((i) => invoiceMatchesBucket(i, 'sent')).length,
		all: invoices.length,
	};
};
